import { ExcepcionesLibre } from "./ExcepcionesLibre";
import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, Percent, Gift, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, formatMoney, formatMoney2 } from "../../lib/api";
import { CierreBar, HistorialCierres } from "./CierreIncentivo";

const pctTxt = (v) => (v == null ? "—" : `${Number(v).toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`);

function MargenSelector({ cfg, onSaved }) {
  const [mode, setMode] = useState(cfg.margin_mode);
  const [manual, setManual] = useState(cfg.margin_manual ?? "");
  const m = cfg.margenes || {};
  const opts = [
    { id: "inventario_libre", label: "Margen del inventario de venta libre", value: m.venta_libre },
    { id: "manual", label: "Margen que indico yo", value: null },
  ];
  const save = async (studyId = cfg.study_id || m.estudio_id) => {
    if (mode === "manual" && (manual === "" || Number.isNaN(Number(manual)))) {
      toast.error("Indica el margen manual (%)");
      return;
    }
    try {
      await api.put("/incentivo-grupal/settings", { margin_mode: mode, margin_manual: mode === "manual" ? Number(manual) : null, study_id: studyId || null });
      toast.success("Margen guardado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar el margen");
    }
  };
  return (
    <div className="card p-5" data-testid="margen-selector">
      <div className="flex items-center gap-2 mb-1"><Percent size={15} /><h3 className="font-display font-bold">Margen a utilizar</h3></div>
      <p className="text-xs text-slate-500 mb-2">
        Solo productos de venta libre del inventario del estudio elegido: (valor PVP − valor PMC) / valor PVP del stock
        {m.productos ? ` · ${m.productos.toLocaleString("es-ES")} productos` : ""}. Venta libre = parafarmacia (CN &lt; 400000) y los CN que en tus diarios se venden sobre todo sin receta. Lo decide el propietario.
      </p>
      {cfg.estudios?.length > 0 && (
        <select className="input text-sm py-1.5 mb-3 w-full" value={m.estudio_id || ""} onChange={(e) => save(e.target.value)} data-testid="margen-study-select">
          {cfg.estudios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      <div className="space-y-2">
        {opts.map((o) => (
          <label key={o.id} className={"flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors " + (mode === o.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}>
            <input type="radio" name="margen" checked={mode === o.id} onChange={() => setMode(o.id)} data-testid={`margen-mode-${o.id}`} />
            <span className="text-sm flex-1">{o.label}</span>
            {o.id === "manual" ? (
              <input type="number" min="0" max="100" step="0.1" value={manual} onChange={(e) => { setManual(e.target.value); setMode("manual"); }}
                     className="input text-sm py-1" style={{ width: 90 }} placeholder="%" data-testid="margen-manual-input" />
            ) : <span className="font-mono text-sm font-semibold">{pctTxt(o.value)}</span>}
          </label>
        ))}
      </div>
      <button onClick={() => save()} disabled={mode === "manual" && manual === ""} className="btn btn-primary mt-3" data-testid="margen-save-btn"><Save size={14} /> Guardar margen</button>
    </div>
  );
}

function PrevManual({ mes, onSaved }) {
  const [v, setV] = useState("");
  const save = async () => {
    try {
      await api.put("/incentivo-grupal/prev", { mes, venta_libre: Number(v) });
      toast.success("Venta del año anterior guardada");
      onSaved();
    } catch {
      toast.error("Indica un importe válido");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="prev-manual">
      <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} className="input text-sm py-1.5" style={{ width: 160 }}
             placeholder={`Venta libre ${mes} (€)`} data-testid="prev-manual-input" />
      <button onClick={save} disabled={!v} className="btn btn-outline text-sm" data-testid="prev-manual-save">Guardar</button>
    </div>
  );
}

function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className={`card p-4 ${accent || ""}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export const IncentivoGrupal = ({ importId }) => {
  const [cfg, setCfg] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [refresh, setRefresh] = useState(0);
  const load = () => {
    api.get("/incentivo-grupal/settings").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/incentivo-grupal", { params: { import_id: importId } })
      .then((r) => { setD(r.data); setErr(""); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo calcular"));
  };
  useEffect(load, [importId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="card p-4 text-sm text-red-700 border-red-300 bg-red-50" data-testid="grupal-error">{err}</div>;
  if (!d || !cfg) return <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Calculando…</div>;
  const sinAnterior = d.venta_libre_anterior == null;

  return (
    <div data-testid="incentivo-grupal">
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">
        Venta libre de todo el equipo en <b>{d.mes_label}</b> frente al mismo mes del año anterior ({d.mes_anterior}). Si crece, se reparte una parte del
        <b> beneficio extra</b> generado solo por la subida: venta extra × margen.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {d.tramos.slice().reverse().map(([min, rep]) => (
          <div key={min} className={"rounded-lg border px-3 py-2 text-sm " + (d.tramo.minimo === min ? "border-emerald-500 bg-emerald-50 font-semibold" : "border-slate-200 bg-white")} data-testid={`tramo-${min}`}>
            Crece ≥ {min} % → reparte <b>{rep} %</b>
          </div>
        ))}
        <div className="rounded-lg border-2 border-slate-900 bg-slate-900 text-white px-3 py-2 text-sm flex items-center justify-between" data-testid="margen-actual-marker">
          <span className="text-[11px] uppercase tracking-widest">Margen actual</span>
          <span className="font-mono font-bold text-lg whitespace-nowrap">{pctTxt(d.margen)}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Stat label={`Venta libre ${d.mes_anterior}`} value={sinAnterior ? "—" : formatMoney(d.venta_libre_anterior)}
              sub={sinAnterior ? "no hay subida de ese mes" : d.fuente_anterior === "manual" ? "importe indicado a mano" : "de la subida del año anterior"} testid="grupal-anterior" />
        <Stat label={`Venta libre ${d.mes}`} value={formatMoney(d.venta_libre_actual)} testid="grupal-actual" />
        <Stat label="Crecimiento" value={pctTxt(d.crecimiento)} accent={d.crecimiento > 0 ? "border-emerald-300" : ""}
              sub={sinAnterior ? "sin dato del año anterior" : d.tramo.reparto ? `tramo ≥ ${d.tramo.minimo} %` : "no llega al 5 %"} testid="grupal-crecimiento" />
        <Stat label="Beneficio extra" value={formatMoney2(d.beneficio_extra)} sub={d.venta_extra > 0 ? `${formatMoney(d.venta_extra)} × ${pctTxt(d.margen)}` : "sin venta extra"} testid="grupal-beneficio" />
        <Stat label="Bolsa a repartir" value={formatMoney2(d.bolsa)} sub={`${d.tramo.reparto} % del beneficio extra`} accent="border-2 border-emerald-500 bg-emerald-50/50" testid="grupal-bolsa" />
      </div>

      <CierreBar d={d} importId={importId} onClosed={() => { load(); setRefresh((x) => x + 1); }} />
      {sinAnterior && !d.cerrado && (
        <div className="card p-4 mb-6 border-amber-300 bg-amber-50/60 text-sm text-amber-900" data-testid="grupal-sin-anterior">
          No has subido el diario de {d.mes_anterior}. Súbelo en Subidas → Ventas mes a mes, o indica aquí la venta libre de ese mes:
          <PrevManual mes={d.mes_anterior} onSaved={load} />
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="card p-5" data-testid="grupal-reparto">
          <div className="flex items-center gap-2 mb-3"><Gift size={15} /><h3 className="font-display font-bold">Reparto propuesto</h3></div>
          <p className="text-xs text-slate-500 mb-3">Proporcional a la venta libre de cada empleado en el mes.</p>
          <table className="dense" data-testid="grupal-reparto-table">
            <thead><tr><th>Empleado</th><th className="num">Venta libre</th><th className="num">Peso</th><th className="num">Importe</th></tr></thead>
            <tbody>
              {d.reparto.map((r) => (
                <tr key={r.vendedor} data-testid={`grupal-row-${r.vendedor}`}>
                  <td className="font-semibold">{r.nombre || r.vendedor}</td>
                  <td className="num">{formatMoney2(r.venta_libre)}</td>
                  <td className="num">{pctTxt(r.peso)}</td>
                  <td className="num font-bold text-emerald-700">{formatMoney2(r.importe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-4">
          {!d.cerrado && <MargenSelector key={`${cfg.margin_mode}-${cfg.margin_manual}`} cfg={cfg} onSaved={load} />}
          {!d.cerrado && <ExcepcionesLibre importId={importId} onChange={load} />}
          {(d.excluido_actual > 0 || d.excluido_anterior > 0) && (
            <div className="text-[11px] text-slate-500" data-testid="grupal-excluido">Excepciones descontadas de la venta libre: {formatMoney2(d.excluido_actual || 0)} este mes y {formatMoney2(d.excluido_anterior || 0)} el mismo mes del año anterior{d.fuente_anterior === "manual" ? " (el año anterior lo escribiste a mano: descuéntalo tú si hace falta)" : ""}.</div>
          )}
          <div className="text-[11px] text-slate-500 flex gap-1.5"><TrendingUp size={13} className="shrink-0 mt-0.5" /> La venta libre es la facturación de venta libre del diario de ventas (PVP con IVA).</div>
        </div>
      </div>
      <HistorialCierres refresh={refresh} />
    </div>
  );
};
